import type { InsightRanking } from "@tutor/genui";
import type { NoticeReason, ProgressReport } from "@tutor/protocol";
import type { AlertReason, ParentAlertRow } from "./api";

// Turns the engine's report into the ranking the deterministic card builder takes.
// The thresholds only sort and filter engine figures; no figure is computed here.

export type AlertKind = InsightRanking["alerts"][number];

/** Weekly change above which a skill counts as improving (2 mastery points). */
export const IMPROVING_THRESHOLD = 0.02;
/** Attempts after which slow progress is worth a parent's attention. */
export const ATTENTION_MIN_ATTEMPTS = 5;

const KIND_ORDER: readonly AlertKind[] = ["safety", "frustration", "plateau", "daily_cap"];

/** The card kind of an alert reason, from the alerts list or the live channel. */
export function alertKind(reason: AlertReason | NoticeReason): AlertKind {
  switch (reason) {
    case "safety_low":
    case "safety_high":
    case "safetyLow":
    case "safetyHigh":
      return "safety";
    case "daily_cap":
    case "dailyCap":
      return "daily_cap";
    case "frustration":
    case "plateau":
      return reason;
  }
}

/** Alerts about `profileId` raised inside the report window. */
export function alertsInWindow(
  alerts: readonly ParentAlertRow[],
  report: ProgressReport,
): ParentAlertRow[] {
  const from = Date.parse(report.from);
  const to = Date.parse(report.to);
  return alerts.filter((a) => {
    const at = Date.parse(a.createdAt);
    return a.profileId === report.profileId && at >= from && at < to;
  });
}

/** How many alerts of each kind, for `AlertCount`. */
export function countAlerts(alerts: readonly ParentAlertRow[]): ReadonlyMap<AlertKind, number> {
  const counts = new Map<AlertKind, number>();
  for (const a of alerts) {
    const kind = alertKind(a.reason);
    counts.set(kind, (counts.get(kind) ?? 0) + 1);
  }
  return counts;
}

export function rankSkills(
  report: ProgressReport,
  windowAlerts: readonly ParentAlertRow[],
): InsightRanking {
  const improving = report.skills
    .filter((s) => s.masteryChange > IMPROVING_THRESHOLD)
    .sort((a, b) => b.masteryChange - a.masteryChange || a.skillId.localeCompare(b.skillId))
    .map((s) => s.skillId);
  const needsAttention = report.skills
    .filter((s) => s.attempts >= ATTENTION_MIN_ATTEMPTS && s.masteryChange <= IMPROVING_THRESHOLD)
    .sort((a, b) => a.masteryChange - b.masteryChange || b.attempts - a.attempts)
    .map((s) => s.skillId);
  const counts = countAlerts(windowAlerts);
  return {
    improving,
    needsAttention,
    alerts: KIND_ORDER.filter((kind) => (counts.get(kind) ?? 0) > 0),
  };
}
